// The Higgsfield import (F9b): the typed calls of core/api/src/routes/imports.ts (plans for the import modal, runs
// with their progress) and the make-shots contract, plus what the studio knows about an imported asset version
// (`extern`). Responses are snake_case, bodies camelCase, as the API has them. Fields another builder adds later
// (`extern`, make-shots) are read defensively: when a server does not send them, the screens leave them out.

import type { Api } from "../api.ts";
import { BRAND } from "../brand.ts";

export type ImportFormat = "episode" | "short" | "feature" | "trailer";

export type ImportSeries = { productionId: string } | { newKey: string; name: string; format: ImportFormat };

export type ImportTarget =
  | { kind: "standalone"; name: string; format: ImportFormat }
  | { kind: "episode"; series: ImportSeries; number: number; title?: string };

export interface PlanDefaults {
  as: "episodes" | "standalone";
  format: ImportFormat;
}

export type Suggestion =
  | { kind: "standalone"; name: string; productionId?: string; episode?: number }
  | {
      kind: "episode";
      seriesKey: string;
      seriesName: string;
      number: number;
      productionId: string | null;
      existingEpisode: boolean;
    };

export interface ProjectCounts {
  assets?: number;
  images?: number;
  videos?: number;
  audio?: number;
  folders?: number;
  bytes?: number;
}

export interface PlanItem {
  id: string;
  source_project_id: string;
  name: string;
  counts: ProjectCounts;
  suggestion: Suggestion;
  target: ImportTarget;
  touched: boolean;
  position: number;
  run_id: string | null;
}

export type PlanStatus = "draft" | "confirmed" | "archived";

export interface Plan {
  id: string;
  workspace_id: string;
  source: string;
  source_workspace_id: string | null;
  status: PlanStatus;
  defaults: PlanDefaults;
  created_by: string | null;
  via: string;
  created_at: string;
  confirmed_at: string | null;
  items: PlanItem[];
}

export type PlanSummary = Omit<Plan, "items"> & {
  item_count: number;
  last_run_at: string | null;
  open_runs: number;
};

export interface PlanPatch {
  defaults?: PlanDefaults;
  items?: { id: string; target?: ImportTarget; position?: number }[];
}

export type RunStatus = "open" | "running" | "done" | "failed";

export interface Run {
  id: string;
  workspace_id: string;
  project_id: string;
  episode_id: string | null;
  plan_item_id: string | null;
  source: string;
  source_ref: { workspaceId?: string | null; projectId?: string; name?: string | null };
  status: RunStatus;
  counts: Record<string, unknown>;
  created_by: string | null;
  via: string;
  created_at: string;
  finished_at: string | null;
  archived_at: string | null;
  error: string | null;
  warnings: string[];
  readFromHiggsfield: boolean;
  /** Filmsquare finished reading Higgsfield for this run; null while it reads (or never did) */
  read_finished_at: string | null;
}

export type SourceKind = "folder" | "element" | "asset";

export interface KindCounts {
  queued: number;
  done: number;
  skipped: number;
  failed: number;
}

export interface FailedLine {
  id: string;
  source_kind: SourceKind;
  source_id: string;
  error: string | null;
  attempts: number;
}

export interface RunView {
  run: Run;
  counts: Record<SourceKind, KindCounts>;
  failed: FailedLine[];
}

export interface ConfirmResult {
  plan: Plan;
  runs: Run[];
  created: { productions: number; episodes: number };
}

export interface MakeShotsResult {
  created: number;
  skipped: number;
  shots: { shotId: string; assetVersionId: string }[];
}

/** What came with an imported asset version (contract 2 of T2 ↔ T4): null for work made in Filmsquare. */
export interface ExternInfo {
  source: string;
  model: string | null;
  prompt: string | null;
  promptRendered: string | null;
  promptTruncated: boolean;
  liked: boolean;
  favourite: boolean;
  elementRefs: string[];
  sourceName: string | null;
}

/** Read an `extern` field as the API may send it (camelCase per the contract, snake_case tolerated). */
export function readExtern(v: unknown): ExternInfo | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const s = (a: unknown): string | null => (typeof a === "string" && a.trim() ? a : null);
  const source = s(o.source);
  if (!source) return null;
  const refs = o.elementRefs ?? o.element_refs;
  return {
    source,
    model: s(o.model),
    prompt: s(o.prompt),
    promptRendered: s(o.promptRendered ?? o.prompt_rendered),
    promptTruncated: (o.promptTruncated ?? o.prompt_truncated) === true,
    liked: o.liked === true,
    favourite: o.favourite === true,
    elementRefs: Array.isArray(refs) ? refs.filter((x): x is string => typeof x === "string") : [],
    sourceName: s(o.sourceName ?? o.source_name),
  };
}

// ── what is known per asset version ────────────────────────────────────────────────────────────────────────
// The folder listing carries `extern` per asset-version item once the server sends it (folders-api.ts feeds this);
// the asset detail asks the URL route as a second place. Nothing here is required: no entry, no prompt block.

const externs = new Map<string, ExternInfo>();
const listeners = new Set<() => void>();
let externVersion = 0;

/** Changes each time something new is known (for useSyncExternalStore). */
export const externsVersion = (): number => externVersion;

export function rememberExtern(assetVersionId: string, v: unknown): void {
  const info = readExtern(v);
  if (!info) return;
  externs.set(assetVersionId, info);
  externVersion++;
  for (const l of listeners) l();
}

export const knownExtern = (assetVersionId: string): ExternInfo | null => externs.get(assetVersionId) ?? null;

export function onExternChange(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

// ── the calls ──────────────────────────────────────────────────────────────────────────────────────────────

export function importsApi(api: Api, ws: string) {
  const w = `/workspaces/${ws}`;
  return {
    plans: (status?: PlanStatus) =>
      api
        .request<{ items: PlanSummary[] }>("GET", `${w}/import-plans`, status ? { query: { status } } : {})
        .then((r) => r.items),
    plan: (id: string) => api.request<Plan>("GET", `${w}/import-plans/${id}`),
    patch: (id: string, body: PlanPatch) => api.request<Plan>("PATCH", `${w}/import-plans/${id}`, { body }),
    confirm: (id: string) =>
      api.request<ConfirmResult>("POST", `${w}/import-plans/${id}/confirm`, { body: {} }),
    archivePlan: (id: string) => api.request<Plan>("POST", `${w}/import-plans/${id}/archive`, { body: {} }),
    run: (id: string) => api.request<RunView>("GET", `${w}/import-runs/${id}`),
    retry: (id: string) =>
      api.request<{ run: Run; retried: number }>("POST", `${w}/import-runs/${id}/retry`, { body: {} }),
    projectRuns: (projectId: string) =>
      api.request<{ items: Run[] }>("GET", `${w}/projects/${projectId}/import-runs`).then((r) => r.items),
    makeShots: (
      projectId: string,
      folderId: string,
      body: { episodeId: string; likedOnly?: boolean; assetVersionIds?: string[] },
    ) =>
      api.request<MakeShotsResult>("POST", `${w}/projects/${projectId}/folders/${folderId}/make-shots`, {
        body,
      }),
    /** the asset's `extern`, from the URL route (null when the server does not send it) */
    externOf: (assetVersionId: string) =>
      api
        .request<{ extern?: unknown }>("GET", `${w}/asset-versions/${assetVersionId}/url`)
        .then((r) => readExtern(r.extern)),
  };
}

export type ImportsApi = ReturnType<typeof importsApi>;

// ── words ──────────────────────────────────────────────────────────────────────────────────────────────────

/** "~11 GB", "~640 MB": the size Higgsfield reported, rounded the way people say it. */
export function sizeText(bytes: number | null | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  const gb = bytes / 1e9;
  if (gb >= 10) return `~${Math.round(gb)} GB`;
  if (gb >= 1) return `~${gb.toFixed(1).replace(/\.0$/, "")} GB`;
  const mb = bytes / 1e6;
  return `~${Math.max(1, Math.round(mb))} MB`;
}

/** The copyable sentence for your own Claude (B4): it stages the plan and sends you the link to arrange it. */
export const CLAUDE_IMPORT_SENTENCE = `Import my Higgsfield projects into ${BRAND.name}. List them, stage them as an import plan, and send me the link to arrange them.`;

/** For a confirmed plan whose runs wait: the sentence that has your Claude carry on. */
export const continueSentence = (planId: string | null): string =>
  `Carry on with my Higgsfield import into ${BRAND.name}. The plan${planId ? ` ${planId}` : ""} is confirmed: send the folders, elements and files for each of its runs, then finish them.`;
